// src/components/MaintenanceNotice.tsx
// Modal de aviso de manutenção do GRAIA ENEM.
// Fechamento secreto: segurar Shift e digitar GRAIA (G-R-A-I-A).
// Não há nenhum elemento visual de fechamento — esta é a única saída.

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Construction } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const BYPASS_KEY = 'graia_maintenance_bypass';

// Sequência secreta: Shift pressionado + teclas G R A I A (capturadas via e.key)
const SECRET_SEQUENCE = ['G', 'R', 'A', 'I', 'A'];

interface MaintenanceNoticeProps {
    children: React.ReactNode;
}

const MaintenanceNotice: React.FC<MaintenanceNoticeProps> = ({ children }) => {
    const { isAuthenticated, isAdmin } = useAuth();
    const [maintenanceActive, setMaintenanceActive] = useState(false);
    const [dismissed, setDismissed] = useState(false);
    const keyBufferRef = useRef<string[]>([]);

    // Consultar status de manutenção no backend
    useEffect(() => {
        fetch('/api/maintenance/status')
            .then(r => r.json())
            .then(data => setMaintenanceActive(data.active === true))
            .catch(() => setMaintenanceActive(false));
    }, []);

    // Verificar bypass de sessão já existente
    useEffect(() => {
        if (sessionStorage.getItem(BYPASS_KEY) === 'true') {
            setDismissed(true);
        }
    }, []);

    // Bloquear scroll do body quando modal está visível
    const shouldBlock = maintenanceActive && !dismissed && !(isAuthenticated && isAdmin);
    useEffect(() => {
        if (shouldBlock) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => { document.body.style.overflow = ''; };
    }, [shouldBlock]);

    // Listener de teclado para sequência secreta
    const handleKeyDown = useCallback((e: KeyboardEvent) => {
        if (!shouldBlock) return;

        // Bloquear Esc
        if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            return;
        }

        // Acumular teclas com Shift pressionado (usar e.key para compatibilidade de layout)
        if (e.shiftKey && e.key.length === 1) {
            const buf = [...keyBufferRef.current, e.key].slice(-SECRET_SEQUENCE.length);
            keyBufferRef.current = buf;
            if (buf.join('') === SECRET_SEQUENCE.join('')) {
                keyBufferRef.current = [];
                sessionStorage.setItem(BYPASS_KEY, 'true');
                setDismissed(true);
            }
        } else if (!e.shiftKey) {
            keyBufferRef.current = [];
        }
    }, [shouldBlock]);

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown, true);
        return () => document.removeEventListener('keydown', handleKeyDown, true);
    }, [handleKeyDown]);

    if (!shouldBlock) {
        return <>{children}</>;
    }

    return (
        <>
            {children}

            {/* Overlay com blur */}
            <div
                style={{
                    position: 'fixed',
                    inset: 0,
                    background: 'rgba(0, 0, 0, 0.55)',
                    backdropFilter: 'blur(10px)',
                    WebkitBackdropFilter: 'blur(10px)',
                    zIndex: 99999,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '24px',
                }}
                onClick={e => e.stopPropagation()}
                onMouseDown={e => e.stopPropagation()}
            >
                {/* Modal card */}
                <div
                    style={{
                        background: 'var(--color-surface, #ffffff)',
                        borderRadius: '18px',
                        padding: '40px 36px 36px',
                        maxWidth: '440px',
                        width: '100%',
                        boxShadow: '0 24px 64px rgba(0,0,0,0.28), 0 4px 16px rgba(0,0,0,0.12)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '0',
                        textAlign: 'center',
                        userSelect: 'none',
                    }}
                    onClick={e => e.stopPropagation()}
                >
                    {/* Ícone */}
                    <div style={{
                        width: '72px',
                        height: '72px',
                        borderRadius: '50%',
                        background: 'rgba(212, 179, 93, 0.12)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: '20px',
                        flexShrink: 0,
                    }}>
                        <Construction size={32} color="#D4B35D" />
                    </div>

                    {/* Título */}
                    <h2 style={{
                        margin: '0 0 12px',
                        fontSize: '1.3rem',
                        fontWeight: 700,
                        color: 'var(--color-text-primary, #0f1117)',
                        lineHeight: 1.3,
                    }}>
                        Plataforma em preparação
                    </h2>

                    {/* Mensagem */}
                    <p style={{
                        margin: '0 0 8px',
                        fontSize: '0.95rem',
                        color: 'var(--color-text-secondary, #5a6278)',
                        lineHeight: 1.6,
                    }}>
                        A plataforma GRAIA está em construção e retorna em breve.
                    </p>

                    <p style={{
                        margin: 0,
                        fontSize: '0.88rem',
                        color: 'var(--color-text-tertiary, #8b93a7)',
                        lineHeight: 1.5,
                    }}>
                        Esta etapa é restrita à equipe administrativa.
                    </p>
                </div>
            </div>
        </>
    );
};

export default MaintenanceNotice;
